import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow, type OrientedFlexProps } from '@/components/ui/flex';
import { Text, type TextProps } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSourcesService } from '@/services/sources';
import { cn } from '@/utils/classUtils';
import { useConditionalScrollTo } from '@/utils/scrollUtils';
import { MessageClassification } from '@ditto/shared';
import { Check, Copy, RotateCw, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useEffect, useMemo, useState, type FC, type Ref } from 'react';
import { toast } from 'sonner';
import { useChatCtx } from './ChatContext';
import { FeedbackModal } from './FeedbackModal';
import { FollowUpQuestions } from './FollowUpQuestions';
import { MemoizedMarkdown } from './MemoizedMarkdown';
import { ReferencesSection } from './ReferencesSection';

export const UserMessage: FC<
  OrientedFlexProps & {
    scrollToMessage?: boolean;
  }
> = ({ className, children, scrollToMessage, ...props }) => {
  const scrollToMessageRef = useConditionalScrollTo(scrollToMessage);

  return (
    <FlexRow justifyContent="end">
      <FlexCol
        px="4"
        py="2"
        gap="1"
        elevation="b-100"
        h="fit"
        className={cn('bg-neutral-200 rounded-[18px] max-w-[500px]', className)}
        {...props}
      >
        <FlexCol>
          <Text variant="body" color="primary" className="whitespace-pre-wrap">
            {children}
          </Text>
          <div ref={scrollToMessageRef} />
        </FlexCol>
      </FlexCol>
    </FlexRow>
  );
};

export const AiTextMessage: FC<
  Omit<OrientedFlexProps, 'children' | 'size'> & {
    messageId: string;
    children?: string | undefined;
    onFeedback?: (scoreValue: 'up' | 'down', comment?: string) => void;
    size?: 'base' | 'sm';
    tableView?: boolean;
  }
> = ({
  className,
  children,
  onFeedback,
  messageId,
  size,
  tableView,
  ...props
}) => {
  const {
    setDisplayedFile,
    sources,
    isDoneStreaming,
    parsedAssistantMessages,
    sendMessage,
    showFollowUpQuestions,
  } = useChatCtx();

  const { sources: sourceFiles } = useSourcesService();

  const [copied, setCopied] = useState(false);

  const [feedback, setFeedback] = useState<'up' | 'down' | undefined>(
    undefined
  );
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const handlePositiveFeedback = () => {
    if (feedback === 'up') {
      setFeedback(undefined);
      return;
    }
    setFeedback('up');
    onFeedback?.('up');
  };

  const handleNegativeFeedback = () => {
    if (feedback === 'down') {
      setFeedback(undefined);
      return;
    }
    setFeedback('down');
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackModalConfirm = (comment: string) => {
    onFeedback?.('down', comment);
    setIsFeedbackModalOpen(false);
  };

  const messageSources = useMemo(
    () => sources.filter(source => source.messageId === messageId),
    [sources, messageId]
  );

  const messageObject = useMemo(() => {
    return parsedAssistantMessages.find(msg => msg.messageId === messageId);
  }, [parsedAssistantMessages, messageId]);

  const copyToClipboard = () => {
    const contentToCopy = messageObject?.content;
    if (contentToCopy) {
      navigator.clipboard.writeText(contentToCopy).then(() => {
        toast('Copied to clipboard');
        setCopied(true);
      });
    }
  };

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Get follow-up questions from the parsed assistant message
  const followupQuestions = messageObject?.followUpQuestions?.filter(
    (q): q is string => q !== undefined
  );

  const handleQuestionClick = (question: string) => {
    sendMessage({
      text: question,
    });
  };

  const handleSourceClick = (source: (typeof sources)[number]) => () => {
    if (source.type === 'file') {
      const { fileId, pageNumber, quotedText } = source;
      // For shared knowledge files, check if there's an external URL

      const file = sourceFiles.find(file => file.id === fileId);

      setDisplayedFile({
        fileId,
        pageNumber,
        type:
          file?.sourceType === 'SharedKnowledgeFile'
            ? 'sharedKnowledgeFile'
            : 'file',
        quotedTexts: [quotedText],
      });
    } else if (source.type === 'database') {
      // Open database URL in new tab
      window.open(source.databaseUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <FlexCol
      justifyContent="start"
      gap="4"
      w="full"
      className={className}
      {...props}
    >
      <MemoizedMarkdown
        size={size}
        content={messageObject?.content ?? ''}
        sources={messageSources}
        tableView={tableView}
      />
      <FlexRow gap="2" alignItems="center">
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={copyToClipboard}
        >
          {copied ? <Check /> : <Copy />}
        </Button>
        {onFeedback && isDoneStreaming && (
          <FlexRow gap="2" alignItems="center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePositiveFeedback}
                >
                  <ThumbsUp
                    className={
                      feedback === 'up'
                        ? 'fill-neutral-500 text-neutral-500'
                        : 'text-neutral-500'
                    }
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Good answer</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNegativeFeedback}
                >
                  <ThumbsDown
                    className={
                      feedback === 'down'
                        ? 'fill-neutral-500 text-neutral-500'
                        : 'text-neutral-500'
                    }
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Bad answer</TooltipContent>
            </Tooltip>
          </FlexRow>
        )}
      </FlexRow>
      {messageSources.length > 0 && (
        <ReferencesSection
          messageSources={messageSources}
          handleSourceClick={handleSourceClick}
        ></ReferencesSection>
      )}

      {showFollowUpQuestions &&
        followupQuestions &&
        followupQuestions.length > 0 && (
          <div>
            <FollowUpQuestions
              questions={followupQuestions}
              onQuestionClick={handleQuestionClick}
            />
            <Box className="HiddenMargin hidden" minH="5" />
          </div>
        )}

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        onConfirm={handleFeedbackModalConfirm}
      />
      <Box className="AiMsgBottom hidden" minH="5" />
    </FlexCol>
  );
};

export const ErrorMessage: FC<
  TextProps & { ref?: Ref<HTMLDivElement>; onRegenerate?: () => void }
> = ({ ref, children, onRegenerate, ...props }) => {
  return (
    <FlexCol justifyContent="start" className="w-fit" ref={ref}>
      <FlexCol gap="4">
        <Text
          variant="body"
          color="danger"
          className="whitespace-pre-line"
          {...props}
        >
          {children ?? 'An unexpected error occurred.'}
        </Text>
        <Button
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={onRegenerate}
        >
          Regenerate
          <RotateCw />
        </Button>
      </FlexCol>
    </FlexCol>
  );
};

export const ClassificationBlock: FC<
  Omit<OrientedFlexProps, 'children'> & { type: MessageClassification }
> = ({ className, type, ...props }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) {
    return null;
  }

  if (type === MessageClassification.OUT_OF_SCOPE) {
    return (
      <FlexCol
        justifyContent="start"
        gap="4"
        w="full"
        className={className}
        {...props}
      >
        <Box className="relative border border-neutral-300 rounded-lg p-8 bg-[#f5ebe6]">
          <button
            onClick={() => setIsDismissed(true)}
            className="absolute top-4 right-4 text-neutral-700 hover:text-neutral-900 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <FlexCol gap="2">
            <Text variant="body" className="font-medium text-neutral-800">
              The question is outside the scope of Ditto.
            </Text>
            <Text variant="body" className="text-neutral-700">
              If you believe this is an error, please help us improve Ditto and
              let us know at{' '}
              <a
                href="mailto:help@trustditto.com"
                className="text-neutral-900 underline hover:text-neutral-700"
              >
                help@trustditto.com
              </a>
              .
            </Text>
          </FlexCol>
        </Box>
      </FlexCol>
    );
  }

  if (type === MessageClassification.SYSTEM) {
    return (
      <FlexCol
        justifyContent="start"
        gap="4"
        w="full"
        className={className}
        {...props}
      >
        <Box className="relative border border-neutral-300 rounded-lg p-8 bg-blue-50">
          <button
            onClick={() => setIsDismissed(true)}
            className="absolute top-4 right-4 text-blue-700 hover:text-blue-900 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <FlexCol gap="2">
            <Text variant="body" className="text-blue-900">
              Ditto is designed to support QARA professionals by providing
              answers to quality assurance and regulatory affairs queries.
            </Text>
            <Text variant="body" className="text-blue-900">
              Try asking a question like: &quot;What regulatory requirements
              should I consider for an FDA submission?&quot;
            </Text>
          </FlexCol>
        </Box>
      </FlexCol>
    );
  }

  // For VALID_QUESTION or unknown types, return null
  return null;
};
